import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import xLogo from '../../../../assets/branding/x-logo-black.png';
import PersonAvatar, {
  buildPersonAvatarUrl,
  getPersonInitial,
} from '../../avatar/PersonAvatar';
import DsModal from '../../DsModal';
import { ResponseStatusSummary } from '../../workAvailability/ResponseStatusProvider';
import ImagePreviewOverlay from '../../ImagePreviewOverlay';
import ArtistCareerTimelineContent, { normalizeArtistCareerTimeline } from '../../profile/ArtistCareerTimelineContent';
import { buildProfileEventPartDisplay } from './memberEventPartUtils';

const API_URL = process.env.REACT_APP_API_URL;
const ARTIST_PROFILE_IMAGE_PATH = process.env.REACT_APP_ARTIST_PROFILE_IMAGE_PATH;
const ADMIN_PROFILE_IMAGE_PATH = process.env.REACT_APP_ADMIN_PROFILE_IMAGE_PATH;

const joinPath = (base, file) =>
  file ? `${API_URL}${String(base).replace(/\/$/, '')}/${String(file).replace(/^\/+/, '')}` : '';

const buildMemberProfileImageUrl = ({ role, file, root, variant = 'medium' }) =>
  buildPersonAvatarUrl({
    role,
    profilePicture: file,
    variant,
    apiUrl: API_URL,
    root,
  }) || joinPath(root, file);

const defaultResolveArtistDetailUrl = (artistId) => `/admin/workspaces/artist-management/artists/${artistId}`;

export default function MemberProfileModal({
  type,
  artist,
  admin,
  onClose,
  allowArtistLink = false,
  resolveArtistDetailUrl = defaultResolveArtistDetailUrl,
}) {
  const [previewUrl, setPreviewUrl] = useState('');
  const [isCareerTimelineOpen, setIsCareerTimelineOpen] = useState(false);
  const commentRef = useRef(null);
  const [isCommentMulti, setIsCommentMulti] = useState(false);
  const commentText = type === 'artist' ? artist?.comment : admin?.comment;
  const eventMembershipLabel = type === 'artist'
    ? artist?.eventMembershipLabel
    : admin?.eventMembershipLabel;
  const eventPartDisplay = useMemo(() => (
    type === 'artist'
      ? buildProfileEventPartDisplay({
          isUnifiedEventGroupChat: artist?.isUnifiedEventGroupChat === true,
          eventUuids: artist?.eventUuids,
          groupEvents: artist?.groupEvents,
          eventPartAssignments: artist?.eventPartAssignments,
          fallbackPartLabel: artist?.part,
        })
      : { entries: [], shouldShowEventPartBlock: false }
  ), [artist, type]);
  const careerTimeline = useMemo(
    () => (type === 'artist'
      ? normalizeArtistCareerTimeline(artist?.careerTimeline || artist?.career_timeline || [])
      : []),
    [artist, type]
  );
  // メンション経由の payload は name ではなく display_name / artist_name 等で渡ることがあるため、
  // 表示名は複数フィールドから解決する（既存ユーザーの名前が出ない問題の対策）。
  const artistName = artist?.name || artist?.artist_name || artist?.display_name || artist?.full_name || artist?.name_en || '';
  const adminName = admin?.name || admin?.display_name || admin?.full_name || admin?.username || '';
  const careerTimelineTitle = `${artistName || 'アーティスト'}の年表`;

  const snsSource = type === 'artist' ? artist : type === 'admin' ? admin : null;
  const snsLinks = snsSource
    ? [
        { key: 'instagram', url: snsSource.instagram, icon: 'fa-brands fa-instagram', className: 'sns-instagram' },
        { key: 'twitter', url: snsSource.twitter, icon: 'fa-solid fa-x', className: 'sns-twitter' },
        { key: 'facebook', url: snsSource.facebook, icon: 'fa-brands fa-facebook', className: 'sns-facebook' },
        { key: 'youtube', url: snsSource.youtube, icon: 'fa-brands fa-youtube', className: 'sns-youtube' },
        { key: 'hp', url: snsSource.hp, icon: 'fa-solid fa-globe', className: 'sns-hp' },
      ].filter((item) => item.url)
    : [];

  const showArtistLink = allowArtistLink && type === 'artist' && artist?.id;
  const goArtistDetail = () => {
    if (!showArtistLink || typeof resolveArtistDetailUrl !== 'function') return;
    const url = resolveArtistDetailUrl(artist.id);
    if (!url) return;
    window.location.href = url;
  };

  const openPreview = (url) => {
    if (!url) return;
    setPreviewUrl(url);
  };
  const closePreview = () => setPreviewUrl('');

  const measureComment = () => {
    const el = commentRef.current;
    if (!el) return;
    const range = document.createRange();
    range.selectNodeContents(el);
    const rects = range.getClientRects();
    if (rects.length > 1) {
      setIsCommentMulti(true);
      return;
    }
    const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight);
    if (Number.isFinite(lineHeight) && lineHeight > 0) {
      setIsCommentMulti(el.getBoundingClientRect().height > lineHeight * 1.2);
      return;
    }
    setIsCommentMulti(false);
  };

  useLayoutEffect(() => {
    if (!commentText) {
      setIsCommentMulti(false);
      return;
    }
    measureComment();
  }, [commentText]);

  useEffect(() => {
    if (!commentText) return undefined;
    const handleResize = () => measureComment();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [commentText]);

  return (
    <>
      <DsModal
        open
        onClose={onClose}
        ariaLabel="プロフィール"
        dismissible={!isCareerTimelineOpen}
        showClose={false}
        className="member-profile-modal"
        overlayClassName="member-profile-modal__overlay"
        bodyClassName="ds-modal__body--flush"
      >
        <button type="button" className="member-profile-modal__close" onClick={onClose} aria-label="閉じる">×</button>
        {type === 'artist' && artist ? (
          <div className="member-profile__content">
            <PersonAvatar
              role="artist"
              gender={artist.gender}
              name={artistName}
              profilePicture={artist.pic}
              imageUrl={artist.pic ? buildMemberProfileImageUrl({
                role: 'artist',
                file: artist.pic,
                root: ARTIST_PROFILE_IMAGE_PATH,
              }) : ''}
              fallback={artist.initial || getPersonInitial(artistName, '?')}
              className="member-profile__avatar"
              initialClassName="member-profile__avatar-initial"
              size={90}
              onClick={artist.pic ? () => openPreview(buildMemberProfileImageUrl({
                role: 'artist',
                file: artist.pic,
                root: ARTIST_PROFILE_IMAGE_PATH,
                variant: '',
              })) : undefined}
            />
            {artistName ? (
              <div
                className={`member-profile__name ${showArtistLink ? 'member-profile__name--link' : ''}`}
                onClick={goArtistDetail}
                role={showArtistLink ? 'button' : undefined}
                tabIndex={showArtistLink ? 0 : undefined}
                onKeyDown={(event) => {
                  if (!showArtistLink) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    goArtistDetail();
                  }
                }}
              >
                {artistName}
              </div>
            ) : null}
            {artist.nameEn ? <div className="member-profile__meta member-profile__name-en">{artist.nameEn}</div> : null}
            {artist.part ? <div className="member-profile__part-text">{artist.part}</div> : null}
            {(artist.isChief ||
              artist.isConcertmaster ||
              (Array.isArray(artist.customRoles) && artist.customRoles.length > 0)) && (
              <div className="member-profile__badge-row">
                {artist.isConcertmaster ? (
                  <div className="member-profile__badge member-profile__badge--concertmaster">
                    コンサートマスター
                  </div>
                ) : null}
                {artist.isChief ? <div className="member-profile__badge member-profile__badge--chief">首席</div> : null}
                {Array.isArray(artist.customRoles)
                  ? artist.customRoles.map((role) => (
                      <div
                        key={`${role?.id || role?.name || 'role'}-profile`}
                        className="member-profile__badge member-profile__badge--role"
                      >
                        {role?.name || role?.role_name || '役職'}
                      </div>
                    ))
                  : null}
              </div>
            )}
            {snsLinks.length ? (
              <div className="member-profile__sns">
                {snsLinks.map(({ key, url, icon, className }) => (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={key}
                    className={`member-profile__sns-link ${className || ''}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {key === 'twitter' ? (
                      <img className="sns-x-icon" src={xLogo} alt="X" />
                    ) : (
                      <i className={icon} />
                    )}
                  </a>
                ))}
              </div>
            ) : null}
            {artist.comment ? (
              <div className={`member-profile__comment ${isCommentMulti ? 'is-multiline' : ''}`}>
                <div ref={commentRef} className="member-profile__comment-text">{artist.comment}</div>
              </div>
            ) : null}
            {eventMembershipLabel && !eventPartDisplay.shouldShowEventPartBlock ? (
              <div className="member-profile__event-membership">{eventMembershipLabel}</div>
            ) : null}
            {eventPartDisplay.shouldShowEventPartBlock ? (
              <div className="member-profile__event-part-block">
                <div className="member-profile__event-part-list">
                  {eventPartDisplay.entries.map((entry) => (
                    <div key={`profile-part-${entry.eventUuid}`} className="member-profile__event-part-item">
                      <div className="member-profile__event-part-label">@{entry.eventLabel}</div>
                      <div className="member-profile__event-part-part">{entry.partLabel}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            {careerTimeline.length ? (
              <button
                type="button"
                className="member-profile__timeline-link"
                onClick={() => setIsCareerTimelineOpen(true)}
              >
                {careerTimelineTitle}
              </button>
            ) : null}
          </div>
        ) : null}

        {type === 'admin' && admin ? (
          <div className="member-profile__content">
            <PersonAvatar
              role="admin"
              name={adminName}
              profilePicture={admin.pic}
              imageUrl={admin.pic ? buildMemberProfileImageUrl({
                role: 'admin',
                file: admin.pic,
                root: ADMIN_PROFILE_IMAGE_PATH,
              }) : ''}
              fallback={getPersonInitial(adminName, 'A')}
              className="member-profile__avatar"
              initialClassName="member-profile__avatar-initial"
              size={90}
              onClick={admin.pic ? () => openPreview(buildMemberProfileImageUrl({
                role: 'admin',
                file: admin.pic,
                root: ADMIN_PROFILE_IMAGE_PATH,
                variant: '',
              })) : undefined}
            />
            {adminName ? <div className="member-profile__name">{adminName}</div> : null}
            <ResponseStatusSummary adminUserId={admin.adminUserId || admin.admin_user_id || admin.adminId || admin.id} />
            {admin.nickname ? <div className="member-profile__meta">{admin.nickname}</div> : null}
            {admin.companyName ? <div className="member-profile__meta">{admin.companyName}</div> : null}
            {(admin.position || admin.department) ? (
              <div className="member-profile__badge">
                {[admin.position, admin.department].filter(Boolean).join('｜')}
              </div>
            ) : null}
            {snsLinks.length ? (
              <div className="member-profile__sns">
                {snsLinks.map(({ key, url, icon, className }) => (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={key}
                    className={`member-profile__sns-link ${className || ''}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {key === 'twitter' ? (
                      <img className="sns-x-icon" src={xLogo} alt="X" />
                    ) : (
                      <i className={icon} />
                    )}
                  </a>
                ))}
              </div>
            ) : null}
            {admin.comment ? (
              <div className={`member-profile__comment ${isCommentMulti ? 'is-multiline' : ''}`}>
                <div ref={commentRef} className="member-profile__comment-text">{admin.comment}</div>
              </div>
            ) : null}
            {eventMembershipLabel ? (
              <div className="member-profile__event-membership">{eventMembershipLabel}</div>
            ) : null}
          </div>
        ) : null}
      </DsModal>
      <ImagePreviewOverlay src={previewUrl} alt="プロフィール画像" onClose={closePreview} />
      {isCareerTimelineOpen && careerTimeline.length ? (
        <DsModal
          open
          onClose={() => setIsCareerTimelineOpen(false)}
          title={careerTimelineTitle}
          className="artist-career-modal"
          overlayClassName="artist-career-modal__overlay"
          bodyClassName="ds-modal__body--flush"
        >
          <ArtistCareerTimelineContent
            title={careerTimelineTitle}
            entries={careerTimeline}
            showTitle={false}
          />
        </DsModal>
      ) : null}
    </>
  );
}
